import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withTranslation } from "react-i18next";
import { Form, Typography, Row, Col, Select, Table, Button, Space, InputNumber } from "antd";
import moment from "moment";
import ACTIONS from "../store/action";
import { ApiNames } from "../api/api";
import { selectIsFetching } from "../store/selector";
import { AdminDateFormItem } from "./form/AdminDateFormItem";
import { UnitFormItem } from "./form/UnitFormItem";
import { DosageFormItem } from "./form/DosageFormItem";
import ResetIcon from "../assets/svg/ResetIcon";
import "./PatientCyclesEditor.css";
import EllipseNotEditableIcon from "../assets/svg/EllipseNotEditableIcon";
import { SORT_DIRECTIONS, ASCEND } from "../constant/sort";
import { NumericalInput } from "./form/NumericalInput";
import { SEC_FONT_GREY } from "../constant/colors";
import { TableStyle } from "./SavedList/TableStyle";
import { PatientViewTableStyle } from "./SavedList/PatientViewTableStyle";
import { scrollTableToTop } from "../utils/misc";

const { Text } = Typography;
const { Option } = Select;
const INVALID_DATE = "-";

const BEFORE_KEY = "Before";
const AFTER_KEY = "After";
const cyclesId = "cycles-editor";

const StyledTable = styled(Table)`
  ${TableStyle}
  ${PatientViewTableStyle}
  .ant-table {
    margin: 0;
    overflow-x: auto;
    max-height: 470px;
  }

  .ant-table-tbody > tr > td {
    height: 43px !important;
  }

  .ant-table-tbody > tr > td:nth-child(2) {
    padding: 8px !important;
  }
`;

const StyledRow = styled(Row)`
  .ant-row {
    display: block;
  }
`;

const FormItemStyled = styled(Form.Item)`
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional) {
    &::before {
      display: none;
    }
    &::after {
      display: inline-block;
      margin-right: 4px;
      line-height: 1;
      content: "*";
      color: ${SEC_FONT_GREY};
    }
  }
`;

const isOfterOrBefore = (daysBefore) => (daysBefore > 0 ? AFTER_KEY : BEFORE_KEY);

const PatientCyclesEditorComponent = ({ t, patient, cycles, isFetching, setEdited, bulkChangeAct }) => {
  const [data, setData] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    setData(cycles);
  }, [cycles]);

  const createNewCycles = (values) =>
    data.map((cycle) => {
      const orderIndexDiff = cycle.orderIndex - values.fromCycle + 1;
      if (values.fromCycle - 1 > cycle.orderIndex) return cycle;

      const newAdministrationDate = moment(values.administrationDate).add(orderIndexDiff * values.daysBetween, "days");
      const newOrderDate =
        values.before_after === BEFORE_KEY
          ? moment(newAdministrationDate).subtract(values.daysBefore, "days")
          : moment(newAdministrationDate).add(values.daysBefore, "days");
      return {
        ...cycle,
        administrationDate: moment(newAdministrationDate).toISOString(),
        orderDate: newOrderDate.toISOString(),
        drugDosage: values.drugDosage,
        dosageUnit: values.dosageUnit,
        quantity: values.quantity,
        totalAmount: (cycle.unitCost * values.quantity).toFixed(2) || ""
      };
    });

  const onSave = (values) => {
    setEdited(false);
    let pharmacyPatient = {
      id: patient.currentOrder.pharmacyPatient,
      pharmacyPatient: patient.currentOrder.pharmacyPatient,
      orderFrequency: values.daysBetween
    };

    if (patient.currentOrder.orderIndex + 1 === values.fromCycle) {
      const newOrderDate =
        values.before_after === BEFORE_KEY
          ? moment(values.administrationDate).subtract(values.daysBefore, "days")
          : moment(values.administrationDate).add(values.daysBefore, "days");

      pharmacyPatient = {
        ...pharmacyPatient,
        orderDate: newOrderDate.toISOString(),
        drugDosage: values.drugDosage,
        dosageUnit: values.dosageUnit,
        quantity: values.quantity
      };
    }

    const newCycles = createNewCycles(values);
    bulkChangeAct(pharmacyPatient, newCycles);
  };

  const onFromCycleChange = (fromCycle) => {
    const daysBefore = moment(data[fromCycle - 1].orderDate).diff(
      moment(data[fromCycle - 1].administrationDate),
      "days"
    );

    form.setFieldsValue({
      administrationDate: moment(data[fromCycle - 1].administrationDate),
      before_after: isOfterOrBefore(daysBefore),
      daysBefore: Math.abs(daysBefore)
    });
  };

  const preview = async () => {
    try {
      const values = await form.validateFields();
      const newCycles = createNewCycles(values);
      setData(newCycles);
    } catch (error) {
      console.log("Validate Failed:", error);
    }
  };

  const reset = () => {
    const daysBefore = moment(orderDate).diff(moment(administrationDate), "days");
    form.setFieldsValue({
      before_after: isOfterOrBefore(daysBefore),
      fromCycle: orderIndex + 1,
      daysBetween: orderFrequency,
      administrationDate: moment(administrationDate),
      drugDosage: drugDosage,
      quantity: quantity,
      dosageUnit: dosageUnit,
      daysBefore: Math.abs(daysBefore)
    });
    setData(cycles);
  };

  const columns = [
    {
      dataIndex: "currentCycle",
      render: (text, record) => (record.orderIndex === orderIndex ? <EllipseNotEditableIcon /> : null),
      width: 30
    },
    {
      title: t("patient.cycles.cycle_title"),
      dataIndex: "cycle",
      defaultSortOrder: ASCEND,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b) => a.orderIndex - b.orderIndex,
      render: (text, record) => `${record.orderIndex + 1} / ${data.length}`,
      width: 80
    },
    {
      title: t("patient.cycles.order_title"),
      dataIndex: "orderDate",
      width: 100
    },
    {
      title: t("patient.cycles.admin_title"),
      dataIndex: "administrationDate",
      width: 100
    },
    {
      title: t("patient.cycles.drug_title"),
      dataIndex: "tmDisplayName",
      width: 160
    },
    {
      title: t("patient.cycles.dosage_title"),
      dataIndex: "drugDosage"
    },
    {
      title: t("patient.cycles.unit_title"),
      dataIndex: "dosageUnit"
    },
    {
      title: t("patient.cycles.quantity_title"),
      dataIndex: "quantity",
      width: 40
    }
  ];

  const onTableChange = () => {
    scrollTableToTop(cyclesId);
  };

  const { orderFrequency, numberOfCycles, currentOrder, isActive, rxnormDrug } = patient;
  const { orderIndex, drugDosage, dosageUnit, quantity, administrationDate, orderDate } = currentOrder;
  const daysBefore = moment(orderDate).diff(moment(administrationDate), "days");
  return (
    <Form
      form={form}
      initialValues={{
        fromCycle: orderIndex + 1,
        daysBetween: orderFrequency,
        administrationDate: administrationDate ? moment(administrationDate) : null,
        drugDosage: drugDosage,
        quantity: quantity,
        dosageUnit: dosageUnit,
        daysBefore: Math.abs(daysBefore),
        before_after: isOfterOrBefore(daysBefore)
      }}
      onFinish={(values) => onSave(values)}
      onChange={() => setEdited(true)}
    >
      <Row gutter={[32, 32]}>
        <Col span={10}>
          <StyledRow gutter={[16, 16]} style={{ backgroundColor: "#F9FBFF" }}>
            <Col span={12}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FormItemStyled
                  name="fromCycle"
                  colon={false}
                  rules={[{ required: true, message: t("form.from_cycle.error_required") }]}
                  label={<Text strong>{t("form.from_cycle.label")}</Text>}
                  style={{ display: "inline-block" }}
                >
                  <Select
                    showSearch={true}
                    onSelect={(value) => onFromCycleChange(value)}
                    data-tm-id="cet-apply-form-cycle"
                  >
                    {Array.from({ length: numberOfCycles - orderIndex }, (v, k) => k + orderIndex + 1).map((k) => (
                      <Option key={k} value={k}>
                        {k}
                      </Option>
                    ))}
                  </Select>
                </FormItemStyled>
                <FormItemStyled
                  name="daysBetween"
                  colon={false}
                  label={<Text strong>{t("form.days_between.label")}</Text>}
                  style={{ display: "inline-block" }}
                  rules={[
                    { required: true, message: t("form.days_between.error_required") },
                    {
                      type: "number",
                      min: 0,
                      max: 730
                    }
                  ]}
                >
                  <InputNumber data-tm-id="cet-days-btw-cycles" style={{ width: "100%" }} />
                </FormItemStyled>
                <DosageFormItem
                  showLabel={true}
                  label={t("form.dosage.label")}
                  strong={true}
                  style={{ display: "inline-block" }}
                  dataTMId="cet-dosage"
                />

                <NumericalInput
                  name="quantity"
                  dataTMId="cct-quantity"
                  defaultValue={quantity}
                  placeholder={t("form.quantity.placeholder")}
                  editable={true}
                  rules={[{ required: true }]}
                  strong={true}
                  showLabel={true}
                  label={t("form.quantity.label")}
                  required={true}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <AdminDateFormItem
                  disabled={true}
                  editable={true}
                  showLabel={true}
                  strong={true}
                  style={{ display: "inline-block" }}
                  dataTMId="cet-admin-date"
                />

                <FormItemStyled
                  label={<Text strong>{t("form.days_before.label")}</Text>}
                  colon={false}
                  required={true}
                  style={{ marginBottom: 0, display: "inline-block" }}
                >
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <FormItemStyled
                      name="before_after"
                      rules={[{ required: true, message: t("new_patient.drug_name_required") }]}
                      style={{
                        display: "inline-block",
                        width: "calc(60% - 5px)",
                        marginRight: 5
                      }}
                    >
                      <Select>
                        <Option key={BEFORE_KEY} value={t("form.days_before.before")}>
                          {t("form.days_before.before")}
                        </Option>
                        <Option key={AFTER_KEY} value={t("form.days_before.after")}>
                          {t("form.days_before.after")}
                        </Option>
                      </Select>
                    </FormItemStyled>
                    <FormItemStyled
                      name="daysBefore"
                      colon={false}
                      style={{ display: "inline-block", width: "40%" }}
                      rules={[
                        { required: true, message: t("form.days_before.error_required") },
                        {
                          type: "number",
                          min: 0,
                          max: 730
                        }
                      ]}
                    >
                      <InputNumber data-tm-id="cet-days-bf-af-admin" style={{ width: "100%" }} />
                    </FormItemStyled>
                  </div>
                </FormItemStyled>
                <UnitFormItem
                  dataTMId="cet-unit"
                  showLabel={true}
                  strong={true}
                  style={{ display: "inline-block", width: "100%" }}
                />
              </div>
            </Col>
          </StyledRow>
          <Button type="text" id="cycle-reset-btn" onClick={() => reset()} data-tm-id="cet-reset-btn">
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <ResetIcon />
              <Text style={{ paddingLeft: 4, color: "#2B56A3" }}>{t("reset")}</Text>
            </div>
          </Button>
        </Col>
        <Col span={14} data-tm-id="cet-preview-container">
          <StyledTable
            id={cyclesId}
            size="small"
            dataSource={data.map((item, index) => ({
              ...item,
              key: index,
              orderDate: item.orderDate ? moment(item.orderDate).format("MMM D, YYYY") : INVALID_DATE,
              administrationDate: item.administrationDate
                ? moment(item.administrationDate).format("MMM D, YYYY")
                : INVALID_DATE,
              tmDisplayName: rxnormDrug && rxnormDrug.tmDisplayName
            }))}
            rowClassName={() => "previw-table"}
            columns={columns}
            pagination={false}
            onChange={onTableChange}
          />
        </Col>
      </Row>
      <Row>
        <Form.Item>
          <Space>
            <Button type="text" id="cycle-edit-preview-btn" onClick={() => preview()} data-tm-id="cet-preview-btn">
              <Text style={{ color: "#2B56A3" }}>{t("preview")}</Text>
            </Button>
            <Button
              disabled={!isActive}
              id="cycle-edit-submit-btn"
              loading={isFetching}
              type="primary"
              htmlType="submit"
            >
              {t("apply")}
            </Button>
          </Space>
        </Form.Item>
      </Row>
    </Form>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([ApiNames.BulkChange])
});

const mapDispatchToProps = (dispatch) => ({
  bulkChangeAct: (pharmacyPatient, orders) => dispatch(ACTIONS.bulkChangeAction(pharmacyPatient, orders))
});

const PatientCyclesEditor = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(PatientCyclesEditorComponent));

export { PatientCyclesEditor };
